













































































































































































































































































































































import axios from 'axios';

import EventBus from '@/eventBus';
import CoButton from '@/components/Atoms/co-button/CoButton.vue';
import CoUser from '@/components/Molecules/co-user/CoUser.vue';
import CoHeadline from '@/components/Atoms/co-headline/CoHeadline.vue';
import CoIcon from '@/components/Atoms/co-icon/CoIcon.vue';
import CoSkeleton from '@/components/Atoms/co-skeleton/CoSkeleton.vue';
import CoText from '@/components/Atoms/co-text/CoText.vue';
import CoDate from '@/components/Molecules/co-date/CoDate.vue';
import CoCard from '@/components/Molecules/co-card/CoCard.vue';
import CoLink from '@/components/Atoms/co-link/CoLink.vue';
import ensureStripePricingTableScript from '@/utils/stripePricingTable';

type SpaceBillingStatusResponse = {
    hasRunningSubscription: boolean;
    canManageSubscription: boolean;
    customerPortalURL?: string;
    customerSessionClientSecret?: string;
    publishableKey?: string;
    pricingTableID?: string;
};

const defaultBillingStatus = (): SpaceBillingStatusResponse => ({
    hasRunningSubscription: false,
    canManageSubscription: false,
    customerPortalURL: '',
    customerSessionClientSecret: '',
    publishableKey: '',
    pricingTableID: '',
});

export default {
    name: 'AdminSettingsAccount',
    components: {
        CoButton,
        CoCard,
        CoDate,
        CoHeadline,
        CoIcon,
        CoSkeleton,
        CoText,
        CoUser,
        CoLink,
    },
    data() {
        return {
            billingAdmin: null,
            billingStatus: defaultBillingStatus(),
            loading: false,
            portalRequestInProgress: false,
            space: this.$store?.state?.space_configuration_admin || {},
        };
    },
    computed: {
        isSuspended() {
            return this.space && this.space.Suspended === true;
        },
        planName() {
            if (this.space?.PlanName?.length > 1) {
                return this.space.PlanName.charAt(0).toUpperCase() + this.space.PlanName.slice(1);
            }

            return this.$t('labels.legacypricing');
        },
        canManageSubscription() {
            return this.billingStatus.canManageSubscription;
        },
        canOpenCustomerPortal() {
            if (!this.billingStatus) {
                return false;
            }

            return Boolean(
                !this.portalRequestInProgress &&
                    this.billingStatus.canManageSubscription &&
                    this.billingStatus.customerPortalURL
            );
        },
        communityIsCancelled() {
            if (!this.space || !this.space.SubscriptionCanceledTo) return false;
            const now = Date.now() / 1000;
            return this.space.SubscriptionCanceledTo < now;
        },
        communityIsCancelledSoon() {
            if (!this.hasRunningSubscription) return false;
            if (!this.space || !this.space.SubscriptionCanceledTo) return false;
            const now = Date.now() / 1000;
            const cancellationTime = this.space.SubscriptionCanceledTo;
            const timeUntilCancellation = cancellationTime - now;
            const sevenDaysInSeconds = 7 * 24 * 60 * 60;
            return timeUntilCancellation > 0 && timeUntilCancellation <= sevenDaysInSeconds;
        },

        hasPricingTableConfiguration() {
            return (
                this.billingStatus.customerSessionClientSecret &&
                this.billingStatus.publishableKey &&
                this.billingStatus.pricingTableID
            );
        },
        hasRunningSubscription() {
            return this.billingStatus.hasRunningSubscription;
        },
        shouldShowPricingTable() {
            return (
                !this.hasRunningSubscription &&
                this.billingStatus.canManageSubscription &&
                this.hasPricingTableConfiguration &&
                this.billingStatus.customerSessionClientSecret
            );
        },
    },
    mounted() {
        this.getAdminSpaceSettings();
        ensureStripePricingTableScript();
    },
    methods: {
        getAdminSpaceSettings() {
            this.loading = true;
            axios({
                method: 'GET',
                url: '/admin/space/settings',
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then((response) => {
                    if (response && response.data) {
                        this.$store.commit('SET_SPACE_OBJECT_FOR_ADMIN', response.data);
                        this.space = response.data;
                        this.getAccountSettings();
                    } else {
                        const message = {
                            Message: this.$t('messages.billingAdminDataLoadFailed'),
                            Details: '',
                        };
                        EventBus.$emit('ERROR', message);
                        this.loading = false;
                    }
                })
                .catch((error) => {
                    const message = {
                        Message: this.$t('messages.billingAdminDataLoadFailed'),
                        Details: error?.response?.data || '',
                    };
                    EventBus.$emit('ERROR', message);
                    this.loading = false;
                });
        },
        getAccountSettings() {
            let billingAdminLoading = true;
            let billingStatusLoading = true;
            this.loading = true;

            // Billing status now controls both portal access and pricing table visibility.
            axios
                .get('/admin/space/billing/status')
                .then((response) => {
                    this.billingStatus = {
                        ...defaultBillingStatus(),
                        ...(response?.data || {}),
                    };
                })
                .catch((error) => {
                    const message = {
                        Message: this.$t('messages.billingStatusLoadFailed'),
                        Details: error?.response?.data || '',
                    };
                    EventBus.$emit('ERROR', message);
                })
                .finally(() => {
                    billingStatusLoading = false;
                    if (!billingStatusLoading && !billingAdminLoading) {
                        this.loading = false;
                    }
                });

            if (this.space.PaymentMethodeCreatedBy) {
                this.$store
                    .dispatch('adminGetUserByID', this.space.PaymentMethodeCreatedBy)
                    .then((response) => {
                        this.billingAdmin = response;
                    })
                    .catch((error) => {
                        const message = {
                            Message: this.$t('messages.billingAdminDataLoadFailed'),
                            Details: error?.response?.data || '',
                        };
                        EventBus.$emit('ERROR', message);
                    })
                    .finally(() => {
                        billingAdminLoading = false;
                        if (!billingStatusLoading && !billingAdminLoading) {
                            this.loading = false;
                        }
                    });
            } else {
                billingAdminLoading = false;
                if (!billingStatusLoading && !billingAdminLoading) {
                    this.loading = false;
                }
            }
        },
        async openCustomerPortalURL() {
            if (!this.canOpenCustomerPortal || this.portalRequestInProgress) {
                return;
            }

            // Prevent repeated interactions from creating concurrent portal sessions while fetching a fresh URL.
            this.portalRequestInProgress = true;
            try {
                const response = await axios.get('/admin/space/billing/status');
                this.billingStatus = {
                    ...defaultBillingStatus(),
                    ...(response?.data || {}),
                };

                if (this.billingStatus.customerPortalURL && this.billingStatus.customerPortalURL.length > 0) {
                    window.location.assign(this.billingStatus.customerPortalURL);
                    return;
                }

                const error = {
                    Message: this.$t('messages.customerPortalUrlMissing'),
                    Details: '',
                };
                EventBus.$emit('ERROR', error);
            } catch (error) {
                const message = {
                    Message: this.$t('messages.customerPortalUrlFailed'),
                    Details: error?.response?.data || '',
                };
                EventBus.$emit('ERROR', message);
            } finally {
                this.portalRequestInProgress = false;
            }
        },
        openDeactivateCommunitySupport() {
            if (!this.canManageSubscription) {
                return;
            }
            // Community deletion is handled by support, so this opens a prefilled Intercom conversation.
            this.$intercom.showNewMessage(this.$t('messages.deactivateOrDeleteCommunitySupportMessage'));
        },
        handlePortalCardKeydown(event) {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                this.openCustomerPortalURL();
            }
        },
    },
};
